<script setup lang="ts">
import { addCircle, close, arrowBack } from "ionicons/icons";
import {
  IonPage,
  IonContent,
  IonIcon,
  IonList,
  IonSelect,
  IonSelectOption,
  IonNote,
  IonLoading,
  IonButton,
  IonGrid,
  IonRow,
  IonCol,
  IonLabel,
  IonItem,
  IonInput,
  IonFooter,
  onIonViewWillEnter,
} from "@ionic/vue";
import { useContextStore } from "@/stores/context";
import router from "@/router";
import countries from "countries-list";
import { useVisitors } from ".";
import { useWindowWidth } from "@/composition/window";

const { isMobile } = useWindowWidth();

const countriesOptions = Object.keys(countries.countries).map((key) => ({
  label: countries.countries[key].name,
  value: key,
}));

const contextStore = useContextStore();

const { state, form, emptyFieldsPresent, loadVisitorTypes, create, clear } =
  useVisitors();

onIonViewWillEnter(() => {
  loadVisitorTypes();
  clear();
});

const submit = () => {
  create().then(() => {
    router.push(`/event/${contextStore.event.id}/visitors`);
    contextStore.addEventRegistration();
  });
};
</script>

<template>
  <ion-page>
    <ion-content>
      <div class="container">
        <div
          class="flex-desktop"
          style="align-items: center"
          :style="isMobile ? '' : 'position: relative; left: -75px;'"
        >
          <ion-fab-button
            v-if="!isMobile"
            color="primary"
            style="margin-right: 20px"
            @click="$router.go(-1)"
          >
            <ion-icon :icon="arrowBack"></ion-icon>
          </ion-fab-button>
          <h1 class="ion-margin-bottom">Add individual visitors</h1>
        </div>
        <ion-list>
          <ion-item :class="{ 'ion-invalid': state.errors.name.length > 0 }">
            <ion-label position="stacked"><b>Name *</b></ion-label>
            <ion-input
              placeholder="John Doe"
              v-model="form.name"
              required
            ></ion-input>
            <ion-note
              slot="error"
              v-for="(message, i) in state.errors.name"
              :key="`name-error-${i}`"
            >
              {{ message }}
            </ion-note>
          </ion-item>
          <ion-item :class="{ 'ion-invalid': state.errors.email.length > 0 }">
            <ion-label position="stacked"><b>Email *</b></ion-label>
            <ion-input
              type="email"
              placeholder="john.doe@gmail.com"
              v-model="form.email"
              required
            >
            </ion-input>
            <ion-note
              slot="error"
              v-for="(message, i) in state.errors.email"
              :key="`email-error-${i}`"
            >
              {{ message }}
            </ion-note>
          </ion-item>
          <ion-item
            :class="{ 'ion-invalid': state.errors.phone_number.length > 0 }"
          >
            <ion-label position="stacked">Phone number</ion-label>
            <ion-input
              type="tel"
              placeholder="+1 123 456 7890"
              v-model="form.phone_number"
            >
            </ion-input>
            <ion-note
              slot="error"
              v-for="(message, i) in state.errors.phone_number"
              :key="`phone-error-${i}`"
            >
              {{ message }}
            </ion-note>
          </ion-item>
          <ion-item
            :class="{ 'ion-invalid': state.errors.birthdate.length > 0 }"
          >
            <ion-label position="stacked">Birth date</ion-label>
            <ion-input
              type="date"
              placeholder="Microsoft"
              v-model="form.birthdate"
            >
            </ion-input>
            <ion-note
              slot="error"
              v-for="(message, i) in state.errors.birthdate"
              :key="`phone-error-${i}`"
            >
              {{ message }}
            </ion-note>
          </ion-item>
          <ion-item :class="{ 'ion-invalid': state.errors.company.length > 0 }">
            <ion-label position="stacked">Company</ion-label>
            <ion-input
              type="text"
              placeholder="Microsoft"
              v-model="form.company"
            >
            </ion-input>
            <ion-note
              slot="error"
              v-for="(message, i) in state.errors.company"
              :key="`company-error-${i}`"
            >
              {{ message }}
            </ion-note>
          </ion-item>
          <ion-item
            :class="{ 'ion-invalid': state.errors.job_title.length > 0 }"
          >
            <ion-label position="stacked">Job title</ion-label>
            <ion-input
              type="text"
              placeholder="Lead Software Developer"
              v-model="form.job_title"
            >
            </ion-input>
            <ion-note
              slot="error"
              v-for="(message, i) in state.errors.job_title"
              :key="`job-error-${i}`"
            >
              {{ message }}
            </ion-note>
          </ion-item>
          <ion-item :class="{ 'ion-invalid': state.errors.country.length > 0 }">
            <ion-label position="stacked">Country</ion-label>
            <ion-select
              placeholder="Select your country"
              v-model="form.country"
            >
              <ion-select-option
                :value="option.value"
                v-for="option in countriesOptions"
                :key="`country-option-${option.value}`"
                >{{ option.label }}</ion-select-option
              >
            </ion-select>
            <ion-note
              slot="error"
              v-for="(message, i) in state.errors.country"
              :key="`job-error-${i}`"
            >
              {{ message }}
            </ion-note>
          </ion-item>

          <ion-item
            v-if="state.visitor_types.length > 0"
            :class="{ 'ion-invalid': state.errors.visitor_type.length > 0 }"
          >
            <ion-label position="stacked">Visitor type</ion-label>
            <ion-select
              placeholder="Select visitor type"
              v-model="form.visitor_type"
            >
              <ion-select-option
                :value="visitor_type.id"
                v-for="visitor_type in state.visitor_types"
                :key="`visitor-type-${visitor_type.id}`"
                >{{ visitor_type.name }}</ion-select-option
              >
            </ion-select>
            <ion-note
              slot="error"
              v-for="(message, i) in state.errors.visitor_type"
              :key="`type-error-${i}`"
            >
              {{ message }}
            </ion-note>
          </ion-item>
        </ion-list>
        <ion-button
          v-if="!isMobile"
          expand="block"
          style="width: 250px; margin: 30px auto"
          color="primary"
          @click="submit()"
          :disabled="emptyFieldsPresent"
        >
          <ion-icon slot="start" :icon="addCircle"></ion-icon>
          Register
        </ion-button>
      </div>
    </ion-content>
    <ion-footer v-if="isMobile">
      <div class="container">
        <section>
          <ion-grid>
            <ion-row>
              <ion-col>
                <ion-button expand="block" color="light" @click="router.back()">
                  <ion-icon slot="start" :icon="close"></ion-icon>
                  Cancel
                </ion-button>
              </ion-col>
              <ion-col>
                <ion-button
                  expand="block"
                  color="primary"
                  @click="submit()"
                  :disabled="emptyFieldsPresent"
                >
                  <ion-icon slot="start" :icon="addCircle"></ion-icon>
                  Register
                </ion-button>
              </ion-col>
            </ion-row>
          </ion-grid>
        </section>
      </div>
    </ion-footer>
    <ion-loading :is-open="state.loading" message="Please wait...">
    </ion-loading>
  </ion-page>
</template>
